import React, { useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { CookieBanner } from '../components/CookieBanner';
import { FloatingContact } from '../components/FloatingContact';
import { useLanguage } from '../contexts/LanguageContext';
import { ContactFormPopup } from '../components/ContactFormPopup';
import { Consultation } from '../components/Consultation';
import { ChevronDownIcon, CheckIcon, ArrowRightIcon, BotIcon, BanknoteIcon, GlobeIcon, ZapIcon, TargetIcon, WrenchIcon, MessageCircleIcon, ClipboardListIcon, StarIcon, QuoteIcon, SparklesIcon, RocketIcon, XCircleIcon, CheckCircle2Icon } from 'lucide-react';
// Memoji images
const MEMOJI_FEMALE_1 = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/nastyatoki.png';
const MEMOJI_MALE = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/yourawesam.png';
const MEMOJI_FEMALE_2 = 'https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/ssemenov.png';
export function AiCoursePage() {
  const {
    t,
    language
  } = useLanguage();
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  // Countdown Timer State
  const [timeLeft, setTimeLeft] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0
  });
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      if (diff > 0) {
        setTimeLeft({
          hours: Math.floor(diff / (1000 * 60 * 60) % 24),
          minutes: Math.floor(diff / 1000 / 60 % 60),
          seconds: Math.floor(diff / 1000 % 60)
        });
      }
    };
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, []);
  // Calculator State
  const [pricePerSite, setPricePerSite] = useState(249);
  const [sitesPerMonth, setSitesPerMonth] = useState(10);
  const monthlyIncome = pricePerSite * sitesPerMonth;
  // FAQ State
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const handleConsultationClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsPopupOpen(true);
  };
  const handleBuyClick = (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    setIsPopupOpen(true);
  };
  // Refs for animations
  const whyRef = useRef(null);
  const isWhyInView = useInView(whyRef, {
    once: true,
    margin: '-100px'
  });
  const faqRef = useRef(null);
  const isFaqInView = useInView(faqRef, {
    once: true,
    margin: '-100px'
  });
  return <div className="min-h-screen w-full" style={{
    backgroundColor: 'var(--bg-primary)'
  }} data-id="element-554">
      <Navigation data-id="element-555" />

      <main data-id="element-556">
        {/* SECTION 1 — HERO */}
        <section className="relative w-full overflow-hidden px-4 pt-24 md:pt-28 pb-12 md:pb-16 flex flex-col min-h-[calc(100vh-80px)] justify-center" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-557">
          {/* Subtle background accent */}
          <div className="absolute inset-0 opacity-20 pointer-events-none" data-id="element-558">
            <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] rounded-full blur-[100px]" style={{
            background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
          }} data-id="element-559" />
          </div>

          {/* Smooth gradient fade at the bottom to prevent sharp cutoff */}
          <div className="absolute bottom-0 left-0 w-full h-32 pointer-events-none z-10" style={{
          background: 'linear-gradient(to top, var(--bg-primary), transparent)'
        }} data-id="element-560" />

          <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col md:flex-row items-center justify-center gap-8 md:gap-12 mt-0 md:mt-8 lg:mt-0" data-id="element-561">
            {/* Left side - Content */}
            <div className="w-full md:flex-1 text-center md:text-left" data-id="element-562">
              {/* Badge */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.1
            }} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full mb-4 md:mb-6 border" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)',
              color: 'var(--text-secondary)'
            }} data-id="element-563">
                <span className="text-xs md:text-sm font-semibold tracking-wide flex items-center gap-1.5" data-id="element-564">
                  🔥 {t('course.hero.badge')}
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.2
            }} className="mb-6 md:mb-8 text-4xl leading-tight tracking-tight md:text-5xl lg:text-[3.5rem] font-bold" style={{
              color: 'var(--text-primary)'
            }} data-id="element-565">
                {t('course.hero.title')}
              </motion.h1>

              {/* ========================================== */}
              {/* DESKTOP ONLY ELEMENTS (Hidden on Mobile)   */}
              {/* ========================================== */}

              {/* Features / Pills - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="hidden md:flex flex-wrap items-center justify-start gap-3 mb-8" data-id="element-566">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-567">
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-568" />

                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-569">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>

              {/* Social Proof - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="hidden md:flex items-center justify-start gap-4 mb-8" data-id="element-570">
                <div className="flex -space-x-3" data-id="element-571">
                  <img src={MEMOJI_FEMALE_1} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-neutral-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-572" />

                  <img src={MEMOJI_MALE} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-green-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-573" />

                  <img src={MEMOJI_FEMALE_2} alt="Student" className="w-10 h-10 rounded-full border-2 object-cover bg-purple-50" style={{
                  borderColor: 'var(--bg-primary)'
                }} data-id="element-574" />
                </div>
                <div className="flex flex-col items-start" data-id="element-575">
                  <div className="flex gap-0.5 mb-0.5" data-id="element-576">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current text-yellow-400" data-id="element-577" />)}
                  </div>
                  <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-578">
                    <strong style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-579">
                      {t('course.hero.socialProofCount')}
                    </strong>{' '}
                    {t('course.hero.socialProof')}
                  </span>
                </div>
              </motion.div>

              {/* Pricing & CTA - Desktop */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="hidden md:flex w-max flex-row items-center gap-8 bg-white rounded-[2rem] p-3 pr-3 pl-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-gray-100 mt-2" data-id="element-580">
                <div className="flex flex-col items-start py-0" data-id="element-581">
                  <span className="text-sm font-semibold mb-1 flex items-center gap-1.5" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-582">
                    🤑 {t('course.hero.specialPrice')}
                  </span>
                  <div className="flex items-baseline gap-3" data-id="element-583">
                    <span className="text-2xl line-through decoration-2 font-medium" style={{
                    color: 'var(--text-muted)'
                  }} data-id="element-584">
                      $249
                    </span>
                    <span className="text-4xl font-bold" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-585">
                      $99
                    </span>
                  </div>
                </div>

                <button onClick={handleBuyClick} className="bg-neutral-900 text-white px-8 py-5 rounded-2xl font-bold text-lg hover:bg-neutral-800 transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 flex items-center justify-center gap-2" data-id="element-586">
                  {t('course.hero.buyCourse')}{' '}
                  <ArrowRightIcon className="w-5 h-5" data-id="element-587" />
                </button>
              </motion.div>

              {/* ========================================== */}
              {/* MOBILE ONLY ELEMENTS (Hidden on Desktop)   */}
              {/* ========================================== */}

              {/* Mobile Card (Video + Pricing + Social Proof) */}
              <motion.div initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              delay: 0.3
            }} className="flex md:hidden flex-col w-full bg-white rounded-3xl shadow-xl border overflow-hidden mb-6" style={{
              borderColor: 'var(--border)'
            }} data-id="element-588">
                {/* Video Section */}
                <div className="w-full pt-6 pb-2" data-id="element-589">
                  <video src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full max-w-[240px] mx-auto h-auto mix-blend-multiply" data-id="element-590" />
                </div>

                <div className="flex flex-col p-5 sm:p-6 pt-0 gap-6" data-id="element-591">
                  {/* Pricing Section */}
                  <div className="flex flex-col items-center" data-id="element-592">
                    <span className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-593">
                      🤑 {t('course.hero.specialPrice')}
                    </span>
                    <div className="flex items-baseline gap-3 mb-6" data-id="element-594">
                      <span className="text-2xl line-through decoration-2 font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-595">
                        $249
                      </span>
                      <span className="text-5xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-596">
                        $99
                      </span>
                    </div>

                    <button onClick={handleBuyClick} className="w-full bg-neutral-900 text-white px-8 py-4 rounded-2xl font-bold text-lg hover:bg-neutral-800 transition-all shadow-md flex items-center justify-center gap-2 mb-6" data-id="element-597">
                      {t('course.hero.buyCourse')}{' '}
                      <ArrowRightIcon className="w-5 h-5" data-id="element-598" />
                    </button>

                    {/* Social Proof - Mobile */}
                    <div className="flex items-center justify-center gap-3 w-full bg-gray-50/50 rounded-2xl p-3 border border-gray-100" data-id="element-599">
                      <div className="flex -space-x-2.5 flex-shrink-0" data-id="element-600">
                        <img src={MEMOJI_FEMALE_1} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-neutral-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-601" />

                        <img src={MEMOJI_MALE} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-green-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-602" />

                        <img src={MEMOJI_FEMALE_2} alt="Student" className="w-8 h-8 rounded-full border-2 object-cover bg-purple-50" style={{
                        borderColor: '#fff'
                      }} data-id="element-603" />
                      </div>
                      <div className="flex flex-col items-start text-left" data-id="element-604">
                        <div className="flex gap-0.5 mb-0.5" data-id="element-605">
                          {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-3 h-3 fill-current text-yellow-400" data-id="element-606" />)}
                        </div>
                        <span className="text-[11px] sm:text-xs font-medium leading-tight" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-607">
                          <strong style={{
                          color: 'var(--text-primary)'
                        }} data-id="element-608">
                            {t('course.hero.socialProofCount')}
                          </strong>{' '}
                          {t('course.hero.socialProof')}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Pills Section - Mobile (Moved outside) */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.4
            }} className="flex md:hidden flex-wrap items-center justify-center gap-2 mb-6" data-id="element-609">
                {[{
                icon: CheckIcon,
                text: t('course.hero.pill1')
              }, {
                icon: ZapIcon,
                text: t('course.hero.pill2')
              }, {
                icon: RocketIcon,
                text: t('course.hero.pill3')
              }].map((item, i) => <div key={i} className="flex items-center gap-2 px-4 py-2 rounded-full border" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-610">
                    <item.icon className="w-4 h-4" style={{
                  color: 'var(--text-muted)'
                }} data-id="element-611" />

                    <span className="text-sm font-medium" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-612">
                      {item.text}
                    </span>
                  </div>)}
              </motion.div>
            </div>

            {/* Right side - Video (Desktop only) */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.8,
            delay: 0.4
          }} className="hidden md:flex md:flex-1 items-center justify-center h-full" data-id="element-613">
              <div className="relative w-full max-w-[480px] mx-auto rounded-3xl overflow-hidden shadow-2xl border bg-white" style={{
              borderColor: 'var(--border)'
            }} data-id="element-614">
                <video src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/WZXutpc9f3.mp4" autoPlay loop muted playsInline className="w-full h-auto" data-id="element-615" />
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 2 — WHY IT'S WORTH IT */}
        <section ref={whyRef} className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-616">
          <div className="relative z-10 mx-auto max-w-6xl" data-id="element-617">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isWhyInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 sm:mb-16 text-center" data-id="element-618">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-619">
                {t('course.recognize.title')}
              </h2>
            </motion.div>

            {/* Cards Layout */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-620">
              {[{
              emoji: '😤',
              title: t('course.recognize.c1.title'),
              text: t('course.recognize.c1.text')
            }, {
              emoji: '💸',
              title: t('course.recognize.c2.title'),
              text: t('course.recognize.c2.text')
            }, {
              emoji: '😰',
              title: t('course.recognize.c3.title'),
              text: t('course.recognize.c3.text')
            }, {
              emoji: '⏳',
              title: t('course.recognize.c4.title'),
              text: t('course.recognize.c4.text')
            }].map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isWhyInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} data-id="element-621">
                  <div className="h-full rounded-3xl border p-6 sm:p-8 flex flex-col items-start gap-4" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-622">
                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl text-3xl" style={{
                  backgroundColor: 'var(--bg-tertiary)'
                }} data-id="element-623">
                      {step.emoji}
                    </div>
                    <div data-id="element-624">
                      <h3 className="mb-2 text-xl font-bold leading-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-625">
                        {step.title}
                      </h3>
                      <p className="text-sm sm:text-base" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-626">
                        {step.text}
                      </p>
                    </div>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 2.5 — WHAT YOU WILL LEARN */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-627">
          <div className="max-w-6xl mx-auto" data-id="element-628">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-629">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-630">
                {t('course.offer.title')}
              </h2>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="relative w-full" data-id="element-631">
              {/* Desktop Unified Background */}
              <div className="hidden lg:block absolute inset-0 rounded-[2.5rem] shadow-xl border pointer-events-none overflow-hidden" style={{
              backgroundColor: 'var(--bg-secondary)',
              borderColor: 'var(--border)'
            }} data-id="element-632">
                <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[100px] opacity-20" style={{
                background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
              }} data-id="element-633" />
              </div>

              <div className="relative z-10 flex flex-col lg:flex-row gap-6 lg:gap-0 items-stretch lg:p-12 xl:p-16" data-id="element-634">
                {/* Left Card */}
                <div className="flex-1 flex flex-col justify-center rounded-3xl lg:rounded-none p-6 sm:p-8 lg:p-0 lg:pr-12 xl:pr-16 border lg:border-none shadow-lg lg:shadow-none" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-635">
                  {/* Tags */}
                  <div className="flex flex-wrap items-center justify-start gap-2 sm:gap-3 mb-6 sm:mb-8" data-id="element-636">
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-637">
                      <span className="text-xs sm:text-sm font-medium" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-638">
                        🖥️ {t('course.offer.tag1')}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-639">
                      <span className="text-xs sm:text-sm font-medium" style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-640">
                        🌱 {t('course.offer.tag2')}
                      </span>
                    </div>
                  </div>

                  {/* Image + Headline */}
                  <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6 mb-6" data-id="element-641">
                    <motion.div initial={{
                    opacity: 0,
                    scale: 0.9
                  }} whileInView={{
                    opacity: 1,
                    scale: 1
                  }} viewport={{
                    once: true
                  }} transition={{
                    duration: 0.8
                  }} className="flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-2xl overflow-hidden flex-shrink-0 shadow-sm border" style={{
                    backgroundColor: 'var(--bg-primary)',
                    borderColor: 'var(--border)'
                  }} data-id="element-642">
                      <img src="https://mirrorful-production.s3.us-west-1.amazonaws.com/patterns/assets/78ty6qptc1z1mad2upktn8/3061a762-a16e-4bb2-8c9d-ff0b99ea8c0f/IMG_4595.png" alt="AI Website Creator" className="w-full h-full object-cover scale-110" data-id="element-643" />
                    </motion.div>
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight tracking-tight" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-644">
                      {t('course.offer.heading')}
                    </h2>
                  </div>

                  {/* Description */}
                  <p className="text-base sm:text-lg md:text-xl leading-relaxed max-w-xl" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-645">
                    {t('course.offer.description')}
                  </p>

                  {/* New Phrase */}
                  <div className="mt-6 sm:mt-8 pt-6 border-t" style={{
                  borderColor: 'var(--border)'
                }} data-id="element-646">
                    <p className="text-base sm:text-lg font-medium leading-relaxed" data-id="element-647">
                      <span style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-648">
                        {t('course.offer.tagline1')}{' '}
                      </span>
                      <span style={{
                      color: 'var(--text-secondary)'
                    }} data-id="element-649">
                        {t('course.offer.tagline2')}
                      </span>
                    </p>
                  </div>
                </div>

                {/* Divider (Desktop only) */}
                <div className="hidden lg:block w-px my-4 opacity-50" style={{
                backgroundColor: 'var(--border)'
              }} data-id="element-650" />

                {/* Right Card: Pricing Integration */}
                <div className="w-full lg:w-[400px] xl:w-[450px] flex flex-col justify-center rounded-3xl lg:rounded-none p-6 sm:p-8 lg:p-0 lg:pl-12 xl:pl-16 border lg:border-none shadow-lg lg:shadow-none" style={{
                backgroundColor: 'var(--bg-secondary)',
                borderColor: 'var(--border)'
              }} data-id="element-651">
                  <ul className="space-y-4 mb-8 sm:mb-10 flex-1" data-id="element-652">
                    {[t('course.offer.feature1'), t('course.offer.feature2'), t('course.offer.feature3'), t('course.offer.feature4')].map((feature, i) => <li key={i} className="flex items-start gap-3" data-id="element-653">
                        <div className="mt-1 flex-shrink-0 w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center" data-id="element-654">
                          <CheckIcon className="w-3 h-3 text-green-500" data-id="element-655" />
                        </div>
                        <span className="text-sm sm:text-base font-medium" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-656">
                          {feature}
                        </span>
                      </li>)}
                  </ul>

                  <div className="mb-6" data-id="element-657">
                    <div className="flex items-baseline gap-2" data-id="element-658">
                      <span className="text-4xl sm:text-5xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-659">
                        {t('course.offer.price')}
                      </span>
                      <span className="text-sm sm:text-base font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-660">
                        {t('course.offer.priceLabel')}
                      </span>
                    </div>
                    <p className="text-sm font-medium mt-2" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-661">
                      {t('course.offer.priceDescription')}
                    </p>
                  </div>

                  <button onClick={handleBuyClick} className="block w-full rounded-2xl py-4 text-center text-base sm:text-lg font-bold transition-all hover:scale-[1.02] shadow-lg" style={{
                  backgroundColor: 'var(--accent)',
                  color: '#FFFFFF'
                }} data-id="element-662">
                    {t('course.offer.cta')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* SECTION 4 — INCOME CALCULATOR */}
        <section className="relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-663">
          <div className="max-w-6xl mx-auto" data-id="element-664">
            <div className="text-center mb-12 sm:mb-16" data-id="element-665">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-666">
                {t('course.calc.title')}
              </h2>
            </div>

            <div className="rounded-[2.5rem] p-6 sm:p-10 lg:p-12 shadow-xl border" style={{
            backgroundColor: 'var(--bg-secondary)',
            borderColor: 'var(--border)'
          }} data-id="element-667">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-668">
                {/* Left: Sliders */}
                <div className="space-y-12" data-id="element-669">
                  {/* Slider 1 */}
                  <div data-id="element-670">
                    <div className="flex justify-between items-end mb-6" data-id="element-671">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-672">
                        {t('course.calc.priceLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-673">
                        ${pricePerSite}
                      </span>
                    </div>
                    <input type="range" min="149" max="700" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 149) / (700 - 149) * 100}%, var(--border) ${(pricePerSite - 149) / (700 - 149) * 100}%)`
                  }} data-id="element-674" />
                  </div>

                  {/* Slider 2 */}
                  <div data-id="element-675">
                    <div className="flex justify-between items-end mb-6" data-id="element-676">
                      <label className="text-lg sm:text-xl font-bold" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-677">
                        {t('course.calc.sitesLabel')}
                      </label>
                      <span className="text-2xl sm:text-3xl font-bold" style={{
                      color: 'var(--accent)'
                    }} data-id="element-678">
                        {sitesPerMonth}
                      </span>
                    </div>
                    <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-3 rounded-full appearance-none cursor-pointer" style={{
                    background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                  }} data-id="element-679" />
                  </div>
                </div>

                {/* Right: Results */}
                <div className="rounded-3xl p-8 sm:p-10 flex flex-col justify-center relative overflow-hidden" style={{
                backgroundColor: 'var(--bg-primary)',
                border: '1px solid var(--border)'
              }} data-id="element-680">
                  {/* Background glow */}
                  <div className="absolute top-0 right-0 w-[300px] h-[300px] rounded-full blur-[80px] opacity-10 pointer-events-none" style={{
                  background: 'radial-gradient(circle, var(--accent) 0%, transparent 70%)'
                }} data-id="element-681" />

                  <div className="relative z-10" data-id="element-682">
                    <p className="text-sm font-bold mb-2 uppercase tracking-wider" style={{
                    color: 'var(--text-secondary)'
                  }} data-id="element-683">
                      {t('course.calc.potentialIncome')}
                    </p>
                    <div className="flex items-baseline gap-2 mb-8 flex-wrap" data-id="element-684">
                      <span className="text-5xl sm:text-6xl lg:text-7xl font-bold break-words" style={{
                      color: 'var(--accent)'
                    }} data-id="element-685">
                        ${monthlyIncome.toLocaleString()}
                      </span>
                      <span className="text-base sm:text-lg font-medium" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-686">
                        {t('course.calc.perMonth')}
                      </span>
                    </div>

                    <div className="space-y-4 mb-10" data-id="element-687">
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-688">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-689">
                          {t('course.calc.coursePrice')}
                        </span>
                        <span className="font-bold" style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-690">
                          $99
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-691">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-692">
                          {t('course.calc.payback')}
                        </span>
                        <span className="font-bold text-green-500" data-id="element-693">
                          {t('course.calc.paybackValue')}
                        </span>
                      </div>
                      <div className="flex justify-between items-center py-3 border-b" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-694">
                        <span className="font-medium" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-695">
                          {t('course.calc.netProfit')}
                        </span>
                        <span className="font-bold" style={{
                        color: 'var(--text-primary)'
                      }} data-id="element-696">
                          ${(monthlyIncome - 99).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <button onClick={handleBuyClick} className="block w-full rounded-2xl py-4 text-center text-lg font-bold transition-all hover:scale-[1.02] shadow-lg" style={{
                    backgroundColor: 'var(--accent)',
                    color: '#FFFFFF'
                  }} data-id="element-697">
                      {t('course.offer.cta')}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 3.5 — TESTIMONIALS */}
        <section className="relative w-full overflow-hidden px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-698">
          <div className="relative z-10 mx-auto max-w-7xl" data-id="element-699">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={true ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 sm:mb-16 text-center" data-id="element-700">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-701">
                {t('course.testimonials.title')}
              </h2>
              <p className="mx-auto max-w-2xl text-sm sm:text-base" style={{
              color: 'var(--text-secondary)'
            }} data-id="element-702">
                {t('course.testimonials.subtitle')}
              </p>
            </motion.div>

            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-703">
              {[{
              name: t('course.testimonials.r1.name'),
              role: t('course.testimonials.r1.role'),
              text: t('course.testimonials.r1.text'),
              image: MEMOJI_FEMALE_1
            }, {
              name: t('course.testimonials.r2.name'),
              role: t('course.testimonials.r2.role'),
              text: t('course.testimonials.r2.text'),
              image: MEMOJI_MALE
            }, {
              name: t('course.testimonials.r3.name'),
              role: t('course.testimonials.r3.role'),
              text: t('course.testimonials.r3.text'),
              image: MEMOJI_FEMALE_2
            }].map((testimonial, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 30
            }} animate={true ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.5,
              delay: 0.1 + index * 0.1
            }} data-id="element-704">
                  <article className="h-full rounded-2xl border p-6 relative flex flex-col" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-705">
                    <QuoteIcon className="absolute top-4 right-4 w-8 h-8 opacity-10" style={{
                  color: 'var(--accent)'
                }} data-id="element-706" />

                    <div className="flex items-center gap-3 mb-4" data-id="element-707">
                      <div className="w-12 h-12 rounded-full overflow-hidden border border-[var(--border)] flex-shrink-0" style={{
                    backgroundColor: 'var(--bg-tertiary)'
                  }} data-id="element-708">
                        <img src={testimonial.image} alt={testimonial.name} className="w-full h-full object-cover" data-id="element-709" />
                      </div>
                      <div data-id="element-710">
                        <p className="font-semibold text-sm" style={{
                      color: 'var(--text-primary)'
                    }} data-id="element-711">
                          {testimonial.name}
                        </p>
                        <p className="text-xs" style={{
                      color: 'var(--text-muted)'
                    }} data-id="element-712">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-1 mb-4" data-id="element-713">
                      {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-4 h-4 fill-current" style={{
                    color: '#FBBF24'
                  }} data-id="element-714" />)}
                    </div>

                    <blockquote className="text-sm sm:text-base leading-relaxed mt-auto" style={{
                  color: 'var(--text-secondary)'
                }} data-id="element-715">
                      "{testimonial.text}"
                    </blockquote>
                  </article>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 3.5 — FEAR VS REALITY */}
        <section className="hidden relative w-full px-4 py-16 sm:py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-716">
          <div className="max-w-5xl mx-auto" data-id="element-717">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.6
          }} className="text-center mb-12 sm:mb-16" data-id="element-718">
              <h2 className="text-3xl font-bold md:text-4xl lg:text-5xl leading-tight" style={{
              color: 'var(--text-primary)'
            }} data-id="element-719">
                {t('course.fear.title1')} <br className="hidden sm:block" data-id="element-720" />
                {t('course.fear.title2')}
              </h2>
            </motion.div>

            <div className="flex flex-col gap-4 sm:gap-6" data-id="element-721">
              {[{
              fear: t('course.fear.f1.fear'),
              reality: t('course.fear.f1.reality')
            }, {
              fear: t('course.fear.f2.fear'),
              reality: t('course.fear.f2.reality')
            }, {
              fear: t('course.fear.f3.fear'),
              reality: t('course.fear.f3.reality')
            }, {
              fear: t('course.fear.f4.fear'),
              reality: t('course.fear.f4.reality')
            }].map((row, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-50px'
            }} transition={{
              duration: 0.4,
              delay: i * 0.1
            }} className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6" data-id="element-722">
                  {/* Fear Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-red-50/50 border-red-100 flex items-center gap-4" data-id="element-723">
                    <div className="w-10 h-10 rounded-full bg-red-100/50 flex items-center justify-center flex-shrink-0" data-id="element-724">
                      <XCircleIcon className="w-5 h-5 text-red-400" data-id="element-725" />
                    </div>
                    <span className="text-base sm:text-lg text-gray-500 line-through decoration-red-300" data-id="element-726">
                      {row.fear}
                    </span>
                  </div>

                  {/* Reality Card */}
                  <div className="p-6 sm:p-8 rounded-3xl border bg-neutral-50/50 border-neutral-200 flex items-center gap-4 shadow-sm" data-id="element-727">
                    <div className="w-10 h-10 rounded-full bg-neutral-100/50 flex items-center justify-center flex-shrink-0" data-id="element-728">
                      <CheckIcon className="w-5 h-5 text-neutral-800" data-id="element-729" />
                    </div>
                    <span className="text-base sm:text-lg font-semibold text-gray-900" data-id="element-730">
                      {row.reality}
                    </span>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 8 — FAQ */}
        <section ref={faqRef} className="relative w-full overflow-hidden px-4 py-24" style={{
        backgroundColor: 'var(--bg-primary)'
      }} data-id="element-731">
          <div className="relative z-10 mx-auto max-w-3xl" data-id="element-732">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={isFaqInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.6
          }} className="mb-12 text-center" data-id="element-733">
              <h2 className="mb-4 text-3xl font-bold md:text-4xl" style={{
              color: 'var(--text-primary)'
            }} data-id="element-734">
                {t('course.faq.title')}
              </h2>
              <p style={{
              color: 'var(--text-secondary)'
            }} data-id="element-735">
                {t('course.faq.subtitle')}
              </p>
            </motion.div>

            <div className="space-y-4" data-id="element-736">
              {[{
              q: t('course.faq.q1'),
              a: t('course.faq.a1')
            }, {
              q: t('course.faq.q2'),
              a: t('course.faq.a2')
            }, {
              q: t('course.faq.q3'),
              a: t('course.faq.a3')
            }, {
              q: t('course.faq.q4'),
              a: t('course.faq.a4')
            }, {
              q: t('course.faq.q5'),
              a: t('course.faq.a5')
            }, {
              q: t('course.faq.q6'),
              a: t('course.faq.a6')
            }].map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} animate={isFaqInView ? {
              opacity: 1,
              y: 0
            } : {}} transition={{
              duration: 0.6,
              delay: index * 0.1
            }} data-id="element-737">
                  <div className="rounded-2xl border overflow-hidden" style={{
                borderColor: 'var(--border)',
                backgroundColor: 'var(--bg-secondary)'
              }} data-id="element-738">
                    <button onClick={() => toggleFaq(index)} className="flex w-full items-center justify-between p-6 text-left transition-colors" aria-expanded={openFaqIndex === index} data-id="element-739">
                      <h3 className="pr-4 font-semibold text-base" style={{
                    color: 'var(--text-primary)'
                  }} data-id="element-740">
                        {item.q}
                      </h3>
                      <motion.div animate={{
                    rotate: openFaqIndex === index ? 180 : 0
                  }} transition={{
                    duration: 0.3
                  }} className="flex-shrink-0" data-id="element-741">
                        <ChevronDownIcon className="h-5 w-5" style={{
                      color: 'var(--accent)'
                    }} data-id="element-742" />
                      </motion.div>
                    </button>

                    <AnimatePresence initial={false} data-id="element-743">
                      {openFaqIndex === index && <motion.div initial={{
                    height: 0,
                    opacity: 0
                  }} animate={{
                    height: 'auto',
                    opacity: 1
                  }} exit={{
                    height: 0,
                    opacity: 0
                  }} transition={{
                    duration: 0.3,
                    ease: 'easeInOut'
                  }} data-id="element-744">
                          <div className="border-t px-6 pb-6 pt-4" style={{
                      borderColor: 'var(--border)'
                    }} data-id="element-745">
                            <p className="leading-relaxed" style={{
                        color: 'var(--text-secondary)'
                      }} data-id="element-746">
                              {item.a}
                            </p>

                            {/* Duplicate Calculator for Question 2 */}
                            {index === 1 && <div className="mt-8" data-id="element-747">
                                <div className="rounded-3xl p-6 sm:p-8 border shadow-md" style={{
                          backgroundColor: 'var(--bg-primary)',
                          borderColor: 'var(--border)'
                        }} data-id="element-748">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center" data-id="element-749">
                                    {/* Left: Sliders */}
                                    <div className="space-y-8" data-id="element-750">
                                      {/* Slider 1 */}
                                      <div data-id="element-751">
                                        <div className="flex justify-between items-end mb-4" data-id="element-752">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-753">
                                            {t('course.calc.priceLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-754">
                                            ${pricePerSite}
                                          </span>
                                        </div>
                                        <input type="range" min="149" max="700" step="50" value={pricePerSite} onChange={e => setPricePerSite(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(pricePerSite - 149) / (700 - 149) * 100}%, var(--border) ${(pricePerSite - 149) / (700 - 149) * 100}%)`
                                }} data-id="element-755" />
                                      </div>

                                      {/* Slider 2 */}
                                      <div data-id="element-756">
                                        <div className="flex justify-between items-end mb-4" data-id="element-757">
                                          <label className="text-sm sm:text-base font-bold" style={{
                                    color: 'var(--text-primary)'
                                  }} data-id="element-758">
                                            {t('course.calc.sitesLabel')}
                                          </label>
                                          <span className="text-lg sm:text-xl font-bold" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-759">
                                            {sitesPerMonth}
                                          </span>
                                        </div>
                                        <input type="range" min="1" max="30" step="1" value={sitesPerMonth} onChange={e => setSitesPerMonth(Number(e.target.value))} className="w-full h-2 rounded-full appearance-none cursor-pointer" style={{
                                  background: `linear-gradient(to right, var(--accent) ${(sitesPerMonth - 1) / (30 - 1) * 100}%, var(--border) ${(sitesPerMonth - 1) / (30 - 1) * 100}%)`
                                }} data-id="element-760" />
                                      </div>
                                    </div>

                                    {/* Right: Results */}
                                    <div className="rounded-2xl p-6 flex flex-col justify-center relative overflow-hidden" style={{
                              backgroundColor: 'var(--bg-secondary)',
                              border: '1px solid var(--border)'
                            }} data-id="element-761">
                                      <div className="relative z-10" data-id="element-762">
                                        <p className="text-xs font-bold mb-2 uppercase tracking-wider" style={{
                                  color: 'var(--text-secondary)'
                                }} data-id="element-763">
                                          {t('course.calc.potentialIncome')}
                                        </p>
                                        <div className="flex items-baseline gap-2 mb-6 flex-wrap" data-id="element-764">
                                          <span className="text-4xl sm:text-5xl font-bold break-words" style={{
                                    color: 'var(--accent)'
                                  }} data-id="element-765">
                                            ${monthlyIncome.toLocaleString()}
                                          </span>
                                          <span className="text-sm font-medium" style={{
                                    color: 'var(--text-muted)'
                                  }} data-id="element-766">
                                            {t('course.calc.perMonth')}
                                          </span>
                                        </div>

                                        <div className="space-y-3 mb-6" data-id="element-767">
                                          <div className="flex justify-between items-center py-2 border-b" style={{
                                    borderColor: 'var(--border)'
                                  }} data-id="element-768">
                                            <span className="text-sm font-medium" style={{
                                      color: 'var(--text-secondary)'
                                    }} data-id="element-769">
                                              {t('course.calc.coursePrice')}
                                            </span>
                                            <span className="text-sm font-bold" style={{
                                      color: 'var(--text-primary)'
                                    }} data-id="element-770">
                                              $99
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-2 border-b" style={{
                                    borderColor: 'var(--border)'
                                  }} data-id="element-771">
                                            <span className="text-sm font-medium" style={{
                                      color: 'var(--text-secondary)'
                                    }} data-id="element-772">
                                              {t('course.calc.payback')}
                                            </span>
                                            <span className="text-sm font-bold text-green-500" data-id="element-773">
                                              {t('course.calc.paybackValue')}
                                            </span>
                                          </div>
                                        </div>

                                        <button onClick={handleBuyClick} className="block w-full rounded-xl py-3 text-center text-sm font-bold transition-all hover:scale-[1.02] shadow-md" style={{
                                  backgroundColor: 'var(--accent)',
                                  color: '#FFFFFF'
                                }} data-id="element-774">
                                          {t('course.offer.cta')}
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>}
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </section>

        {/* SECTION 9 — FINAL CTA (Consultation Component) */}
        <Consultation data-id="element-775" />
      </main>

      <Footer data-id="element-776" />
      <CookieBanner data-id="element-777" />
      <FloatingContact data-id="element-778" />

      <ContactFormPopup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)} data-id="element-779" />
    </div>;
}